/**
 * I pezzi delle viste a schede, nati con l'allenamento e usati anche dalla
 * partita: la riga dei comandi sotto le schede, lo stato del salvataggio a
 * tocco, la card su sfondo grigio, il riquadro bianco dei filtri, la sezione
 * senza card dei grafici (Confronto, Trend, Carichi).
 */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { CircleAlertIcon, CircleCheckIcon, RotateCwIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

/** La riga sotto le schede: a sinistra i numeri della scheda, a destra i suoi comandi. */
export function BarraScheda({
  children,
  comandi,
  className,
}: {
  children?: ReactNode;
  comandi?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm">{children}</div>
      {comandi && <div className="flex flex-wrap items-center gap-2">{comandi}</div>}
    </div>
  );
}

/** Tutto salvato, in corso o fermo sulla rete, con il pulsante per ritentare. */
export function StatoSalvataggio({
  inSospeso,
  nonSalvate,
  onRiprova,
}: {
  inSospeso: number;
  nonSalvate: number;
  onRiprova: () => void;
}) {
  return (
    <span className="flex items-center gap-2 text-[13px]" aria-live="polite">
      {inSospeso === 0 ? (
        <span className="flex items-center gap-1 text-muted-foreground">
          <CircleCheckIcon aria-hidden className="size-4" />
          Tutto salvato
        </span>
      ) : nonSalvate > 0 ? (
        <>
          <span className="flex items-center gap-1 font-semibold text-destructive">
            <CircleAlertIcon aria-hidden className="size-4" />
            {nonSalvate} non salvat{nonSalvate === 1 ? 'o' : 'i'}: controlla la connessione
          </span>
          <Button variant="outline" size="sm" onClick={onRiprova}>
            <RotateCwIcon data-icon="inline-start" />
            Riprova
          </Button>
        </>
      ) : (
        <span className="flex items-center gap-1 text-muted-foreground">
          <Spinner className="size-4" />
          Salvo…
        </span>
      )}
    </span>
  );
}

/** La card su sfondo grigio delle viste: icona, titolo in maiuscolo e conteggio. */
export function CardVista({
  titolo,
  Icona,
  conteggio,
  accanto,
  nota,
  azione,
  children,
  className,
}: {
  titolo: string;
  /** Assente nelle card che dividono una lista, come i reparti della rosa. */
  Icona?: LucideIcon;
  conteggio?: ReactNode;
  /** Dopo il conteggio, sulla riga del titolo: un secondo conteggio. */
  accanto?: ReactNode;
  /** Sotto il titolo, un dato di contorno; una spiegazione va in una `Nota` nel corpo. */
  nota?: ReactNode;
  /** Comandi della card, sotto il titolo. */
  azione?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn('h-full bg-muted', className)}>
      <CardHeader>
        <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
          {Icona && <Icona aria-hidden className="size-5" />}
          <h2>{titolo}</h2>
          {conteggio !== undefined && <Badge className="tabular-nums">{conteggio}</Badge>}
          {accanto}
        </CardTitle>
        {nota && <CardDescription className="text-center">{nota}</CardDescription>}
        {azione && <div className="flex flex-wrap justify-center gap-2 pt-1">{azione}</div>}
      </CardHeader>
      <CardContent className="flex flex-col gap-2 px-2 @xl:px-(--card-spacing)">
        {children}
      </CardContent>
    </Card>
  );
}

/** Filtri o un messaggio in un riquadro bianco: fondo pieno, titolo facoltativo. */
export function Riquadro({ titolo, children }: { titolo?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-lg border bg-card p-3">
      {titolo && <h3 className="flex items-center gap-1.5 font-medium">{titolo}</h3>}
      {children}
    </div>
  );
}

/**
 * Una sezione senza card, per i grafici e per ciò che li accompagna (gli
 * attributi dei Carichi del giocatore): titolo in maiuscolo con l'icona e una
 * riga sotto, i comandi a destra del titolo; il contenuto scorre sullo sfondo
 * della vista.
 */
export function SezioneGrafici({
  titolo,
  Icona,
  azione,
  children,
  className,
}: {
  titolo: string;
  Icona?: LucideIcon;
  /** Comandi della sezione, sulla riga del titolo. */
  azione?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('flex min-w-0 flex-col gap-4', className)}>
      <header className="flex min-h-9 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b pb-2">
        <h2 className="flex items-center gap-2 font-semibold tracking-wide uppercase">
          {Icona && <Icona aria-hidden className="size-5" />}
          {titolo}
        </h2>
        {azione && <div className="flex flex-wrap items-center gap-2">{azione}</div>}
      </header>
      {children}
    </section>
  );
}

/** Un grafico di una sezione, senza bordo: titolo facoltativo con l'icona. */
export function Grafico({ titolo, children }: { titolo?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {titolo && <h3 className="flex items-center gap-1.5 text-sm font-medium">{titolo}</h3>}
      {children}
    </div>
  );
}
